import React, { useState, useRef, useEffect } from 'react';
import { usePermissionFilteredItems } from '../../hooks/usePermissionFilteredItems';

/**
 * @description Dropdown that filters items by permission and hides if empty
 *
 * Key features:
 * - Filters menu items based on user permissions
 * - Hides entire dropdown if no items are visible
 * - Follows industry standard (HubSpot, ClickUp pattern)
 * - Closes on outside click
 *
 * @dependencies
 * - usePermissionFilteredItems: For permission-based filtering
 *
 * @usage
 * <PermissionGatedDropdown
 *   items={[
 *     { label: 'New Product', permission: PERMISSIONS.products.create, onClick: () => openModal('product') },
 *     { label: 'New Category', permission: PERMISSIONS.categories.create, onClick: () => openModal('category') },
 *     { label: 'New Vendor', permission: PERMISSIONS.vendors.create, onClick: () => openModal('vendor') },
 *   ]}
 *   buttonLabel="Create"
 * />
 *
 * @props
 * - items: Array of { label, permission?, onClick, className?, disabled?, key? }
 * - buttonLabel: Text for the dropdown button (default: "Create")
 * - buttonClassName: Custom class for the button
 * - dropdownClassName: Custom class for the dropdown menu
 * - showIcon: Whether to show the plus icon (default: true)
 * - icon: Custom icon component (default: plus icon)
 */
const PermissionGatedDropdown = ({
  items,
  buttonLabel = 'Create',
  buttonClassName,
  dropdownClassName,
  showIcon = true,
  icon,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const visibleItems = usePermissionFilteredItems(items);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Hide entire dropdown if no items are visible
  if (visibleItems.length === 0) {
    return null;
  }

  const defaultButtonClassName = 'bg-brand text-white px-4 py-2 rounded-lg font-semibold flex items-center gap-2 hover:bg-brand-hover transition-colors focus:outline-none';
  const defaultDropdownClassName = 'absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-10';

  const DefaultIcon = () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
    </svg>
  );

  const IconComponent = icon || DefaultIcon;

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={buttonClassName || defaultButtonClassName}
      >
        {showIcon && <IconComponent />}
        {buttonLabel}
      </button>
      {isOpen && (
        <div className={dropdownClassName || defaultDropdownClassName}>
          {visibleItems.map((item, index) => (
            <button
              key={item.key || index}
              onClick={() => {
                item.onClick?.();
                setIsOpen(false);
              }}
              className={item.className || 'block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100'}
              disabled={item.disabled}
            >
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default PermissionGatedDropdown;
